import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import ConversationModal from './ConversationModal';
import { enquiryService, authService } from '../api/axios';
import { AuthContext } from '../context/AuthContext';
import FilePreviewModal from '../components/FilePreviewModal';
import { useSearchParams } from 'react-router-dom';
import TotalValue from '../components/TotalValue';
import AttachmentViewer from '../components/AttachmentViewer';
import { formatDate } from '../utils/DateUtils';

import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Chip,
  Alert,
  Divider,
  Stack,
  TextField,
  Snackbar,
  InputAdornment,
  IconButton,
  Pagination,
  Container
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import '../styles/_utilities.css';
import SCMetrics from './SCMetrics';

function SCDashboard() {
  const { user } = useContext(AuthContext);
  const [enquiries, setEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [selectedEnquiryForOverallConversation, setSelectedEnquiryForOverallConversation] = useState(null);
  const [showOverallEnquiryConversationModal, setShowOverallEnquiryConversationModal] = useState(false);

  const [scmUsers, setScmUsers] = useState([]);
  const [fetchingScmUsersError, setFetchingScmUsersError] = useState('');
  const [bdmUsers, setBdmUsers] = useState([]);
  const [fetchingBdmUsersError, setFetchingBdmUsersError] = useState('');

  const [showFilePreviewModal, setShowFilePreviewModal] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRequestedBy, setFilterRequestedBy] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState(user?.username || '');

  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const scmStatusOptions = ["Pending with SCM", "Pending with BDM", "Closed Won", "Closed Lost", "On Hold", "Pending With Client"];

  const fetchScmUsers = useCallback(async () => {
    try {
        const response = await authService.get('/auth/users', { params: { role: 'SUPPLY_CHAIN' } });
        setScmUsers(response.data.map(u => u.username));
    } catch (err) {
      setFetchingScmUsersError('Failed to load SCM users for filtering.');
    }
  }, []);

  const fetchBdmUsers = useCallback(async () => {
    try {
        const response = await authService.get('/auth/users', { params: { role: 'BUSINESS_DEVELOPMENT' } });
        setBdmUsers(response.data.map(u => u.username));
    } catch (err) {
      setFetchingBdmUsersError('Failed to load BDM users for filtering.');
    }
  }, []);

  const fetchFilteredEnquiries = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError('');
    try {
      const params = {
        assignedTo: filterAssignedTo === 'all' ? undefined : filterAssignedTo,
        requestedBy: filterRequestedBy || undefined,
        status: filterStatus || undefined,
        searchTerm: searchTerm.trim() !== '' ? searchTerm : undefined,
        startDate: startDate ? startDate + 'T00:00:00' : undefined,
        endDate: endDate ? endDate + 'T23:59:59' : undefined,
        page: page - 1,
        size: 10
      };
      const response = await enquiryService.get('/enquiry', { params });
      setEnquiries(Array.isArray(response.data.content) ? response.data.content : []);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError('Failed to load enquiries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user, filterAssignedTo, filterRequestedBy, filterStatus, searchTerm, startDate, endDate, page]);

  const filters = useMemo(() => ({
    status: filterStatus,
    requestedBy: filterRequestedBy,
    assignedTo: filterAssignedTo,
    startDate,
    endDate,
  }), [filterStatus, filterRequestedBy, filterAssignedTo, startDate, endDate]);

  useEffect(() => {
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError('');
      try {
        const params = {
          requestedBy: filters.requestedBy || undefined,
          assignedTo: filters.assignedTo === 'all' ? undefined : filters.assignedTo,
          status: filters.status || undefined,
          startDate: filters.startDate ? `${filters.startDate}T00:00:00` : undefined,
          endDate: filters.endDate ? `${filters.endDate}T23:59:59` : undefined,
        };
        const response = await enquiryService.get('/enquiry/metrics', { params });
        setMetrics(response.data);
      } catch (err) {
        setMetricsError('Failed to load metrics. Please try again later.');
        console.error("Error fetching SC metrics:", err);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [filters]);

  useEffect(() => {
    if (user) {
      fetchScmUsers();
      fetchBdmUsers();
    }
  }, [user, fetchScmUsers, fetchBdmUsers]);

  useEffect(() => {
    if (user && !filterAssignedTo) {
        setFilterAssignedTo(user.username);
    }
    const handler = setTimeout(() => {
        fetchFilteredEnquiries();
    }, 500);
    return () => clearTimeout(handler);
  }, [fetchFilteredEnquiries, user, filterAssignedTo]);


  useEffect(() => {
    const enquiryIdFromUrl = searchParams.get('enquiryId') || localStorage.getItem('redirectEnquiryId');
    if (enquiryIdFromUrl && enquiries.length > 0 && !isLoading) {
      const enquiryToOpen = enquiries.find(enc => enc.uniqueId === enquiryIdFromUrl);
      if (enquiryToOpen) {
        setSelectedEnquiryForOverallConversation(enquiryToOpen);
        setShowOverallEnquiryConversationModal(true);
        localStorage.removeItem('redirectEnquiryId');
        setSearchParams({}, { replace: true });
      }
    }
  }, [enquiries, isLoading, searchParams, setSearchParams]);

  const handleOverallEnquiryAssignment = async (enquiryId, assignedToUsername) => {
    try {
      await enquiryService.put(`/enquiry/${enquiryId}/assign`, null, { params: { assignedTo: assignedToUsername } });
      setSnackbar({ open: true, message: `Enquiry assigned to ${assignedToUsername || 'Unassigned'}.`, severity: 'success' });
      fetchFilteredEnquiries();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to assign enquiry: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
    }
  };

  const handleItemInteraction = (item, enquiry) => {
    setSelectedItemForModal(item);
    setSelectedEnquiryForOverallConversation(enquiry);
  };

  const handleOpenOverallConversation = (enquiry) => {
    setSelectedEnquiryForOverallConversation(enquiry);
    setShowOverallEnquiryConversationModal(true);
  }

  const handleCloseConversationModal = () => {
    setSelectedItemForModal(null);
    setSelectedEnquiryForOverallConversation(null);
    setShowOverallEnquiryConversationModal(false);
    fetchFilteredEnquiries();
  };

  const handleOpenFilePreview = (attachment) => {
    if (!attachment || !attachment.id) return;
    enquiryService.get(`/enquiry/attachments/${attachment.id}/presigned-url`)
      .then(response => {
        setFileToPreview({ url: response.data, name: attachment.originalFileName, type: attachment.contentType });
        setShowFilePreviewModal(true);
      })
      .catch(err => {
        setSnackbar({ open: true, message: `Failed to get file preview: ${err.response?.data?.message || 'Error'}`, severity: 'error' });
      });
  };

  const handleCloseFilePreview = () => {
    setFileToPreview(null);
    setShowFilePreviewModal(false);
  };

  const getStatusChipColor = (status) => {
    const statusLower = status ? status.toLowerCase() : '';
    if (statusLower.includes('closed won')) return 'success';
    if (statusLower.includes('closed lost')) return 'error';
    if (statusLower.includes('pending')) return 'warning';
    if (statusLower.includes('on hold')) return 'info';
    return 'default';
  };

  const handleMetricClick = (filter) => {
    setFilterStatus(filter.status || '');
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterRequestedBy('');
    setStartDate('');
    setEndDate('');
    setFilterAssignedTo(user?.username || '');
    setSearchTerm('');
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (!user) return <Typography sx={{ p: 3, textAlign: 'center' }}><Typography variant="h4" color="error">Access Denied</Typography></Typography>;

  return (
    <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 2, mb: 3, overflowX: 'hidden', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
        <Typography variant="h2" component="h2" sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>
          Supply Chain Dashboard
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
          <TextField
            label="Search Enquiries" variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') fetchFilteredEnquiries(); }}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')} size="small"><CloseIcon /></IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
        </Stack>
      </Box>

      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Enquiries</Typography>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Assigned To</InputLabel>
                    <Select value={filterAssignedTo} label="Assigned To" onChange={(e) => setFilterAssignedTo(e.target.value)}>
                        <MenuItem value={user.username}>Assigned to Me</MenuItem>
                        <MenuItem value="all">All SCM Users</MenuItem>
                        <Divider />
                        {scmUsers.map(name => (<MenuItem key={name} value={name}>{name}</MenuItem>))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Requested By (BDM)</InputLabel>
                    <Select value={filterRequestedBy} label="Requested By (BDM)" onChange={(e) => setFilterRequestedBy(e.target.value)}>
                        <MenuItem value=""><em>All BDMs</em></MenuItem>
                        {bdmUsers.map(name => (<MenuItem key={name} value={name}>{name}</MenuItem>))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                        <MenuItem value=""><em>All Statuses</em></MenuItem>
                        {scmStatusOptions.map(status => (<MenuItem key={status} value={status}>{status}</MenuItem>))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} container alignItems="center" spacing={2}>
                 <Grid item xs={6}>
                    <TextField label="From" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth/>
                 </Grid>
                 <Grid item xs={6}>
                    <TextField label="To" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth/>
                 </Grid>
            </Grid>
            <Grid item xs={12} container justifyContent="flex-end">
                <Button variant="outlined" onClick={handleClearFilters}>Clear Filters</Button>
            </Grid>
        </Grid>
      </Paper>

      {fetchingScmUsersError && <Alert severity="error" sx={{ mb: 2 }}>{fetchingScmUsersError}</Alert>}
      {fetchingBdmUsersError && <Alert severity="error" sx={{ mb: 2 }}>{fetchingBdmUsersError}</Alert>}

      {/* Metrics Component (Includes Total Value now) */}
      <SCMetrics
        metrics={metrics}
        isLoading={metricsLoading}
        error={metricsError}
        onMetricClick={handleMetricClick}
      />
      <TotalValue value={metrics?.totalValue} />

      <Typography variant="h3" component="h3" sx={{ mb: 2, mt: 4 }}>
        {filterAssignedTo === user.username ? 'My Assigned Enquiries' : 'Enquiries'}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : enquiries.length === 0 ? (
        <Typography sx={{ mb: 2, textAlign: 'center', p: 3 }}>No enquiries found based on the current filters.</Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0 }}>
          {enquiries.map(enquiry => {
            const enquiryValue = enquiry.items ? enquiry.items.reduce((itemSum, item) => itemSum + (item.quantity || 0) * (item.budgetPerUnit || 0), 0) : 0;

            return (
            <Paper key={enquiry.id} sx={{ p: 2, mb: 3, boxShadow: 1, width: '100%' }}>
              <Typography variant="h3" sx={{ mb: 1.5, wordBreak: 'break-all' }}>{enquiry.uniqueId}</Typography>
              <Grid container spacing={1} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                          <strong>Client:</strong> {enquiry.clientName}
                      </Typography>
                  </Grid>
                  {/* ADDED: Lead Source Display */}
                  <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                          <strong>Lead Source:</strong> {enquiry.leadSource || 'N/A'}
                      </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                          <strong>Requested By:</strong> {enquiry.requestedBy}
                      </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Typography variant="enquiryValue">
                          <strong>Enquiry Value:</strong> {enquiryValue.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                      </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                      <Typography variant="body1">
                          Created: {formatDate(enquiry.createdAt)}
                      </Typography>
                  </Grid>

                  {/* Added Lost Reason Display */}
                  {enquiry.status === 'Closed Lost' && enquiry.lostReason && (
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body1" color="error">
                            <strong>Lost Reason:</strong> {enquiry.lostReason}
                        </Typography>
                    </Grid>
                  )}

              </Grid>
              <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Typography component="span" variant="body2" fontWeight="bold">Overall Status:</Typography>
                  <Chip label={enquiry.status} color={getStatusChipColor(enquiry.status)} size="small" />
                  <Typography component="span" variant="body2" fontWeight="bold" ml={{sm: 2}}>Assigned To:</Typography>
                   <FormControl size="small" sx={{ minWidth: 150 }}>
                       <InputLabel>Assign</InputLabel>
                       <Select value={enquiry.assignedTo || ''} label="Assign" onChange={(e) => handleOverallEnquiryAssignment(enquiry.id, e.target.value)}>
                           <MenuItem value=""><em>Unassigned</em></MenuItem>
                           {scmUsers.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                       </Select>
                   </FormControl>
                   <Button size="small" variant="outlined" onClick={() => handleOpenOverallConversation(enquiry)} sx={{ml: {sm: 2}, flexShrink: 0}}>View Overall Conversation</Button>
              </Stack>
              <AttachmentViewer attachments={enquiry.imageAttachments} onOpenFilePreview={handleOpenFilePreview} />
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                {enquiry.items.map(item => (
                  <Grid item xs={12} sm={6} md={4} key={item.id} sx={{ width: '100%' }}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <Box>
                        {/* FIX 5: Added wordBreak to Item Name */}
                        <Typography variant="h4" component="h4" mb={1.5} sx={{ wordBreak: 'break-all' }}>{item.name}</Typography>
                        <Grid container spacing={0.5}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.5,
                                        mb: 0.5,
                                        wordBreak: 'break-all'
                                    }}
                                >
                                  {item.description}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2"><strong>Quantity:</strong> {item.quantity || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                    <strong>Budget Per Unit:</strong> {
                                        item.budgetPerUnit ?
                                        item.budgetPerUnit.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }) :
                                        'N/A'
                                    }
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2"><strong>Delivery Location:</strong> {item.deliveryLocation || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2">
                                    <strong>Target Date:</strong> {formatDate(item.targetDateOfDelivery)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Status:</strong> <Chip label={item.status} color={getStatusChipColor(item.status)} size="small" sx={{ ml: 1 }} />
                                </Typography>
                            </Grid>
                        </Grid>
                        <Box sx={{
                            maxHeight: 70,
                            minHeight: 70,
                            overflowY: 'auto',
                            mb: 1,
                            pr: 1
                        }}>
                            <AttachmentViewer attachments={item.imageAttachments} onOpenFilePreview={handleOpenFilePreview} />
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2 }}>
                        <Button onClick={() => handleItemInteraction(item, enquiry)} variant="outlined" color="primary" fullWidth size="small">
                          Respond
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      {selectedItemForModal && selectedEnquiryForOverallConversation && (
        <ConversationModal
          enquiryItem={selectedItemForModal}
          enquiry={selectedEnquiryForOverallConversation}
          onClose={handleCloseConversationModal}
          userRole={user.role}
          onOpenFilePreview={handleOpenFilePreview}
        />
      )}
      {showOverallEnquiryConversationModal && !selectedItemForModal && (
        <ConversationModal
          enquiry={selectedEnquiryForOverallConversation}
          enquiryItem={null}
          onClose={handleCloseConversationModal}
          userRole={user.role}
          onOpenFilePreview={handleOpenFilePreview}
          isEnquiryLevel={true}
        />
      )}
      {showFilePreviewModal && fileToPreview && (
        <FilePreviewModal fileUrl={fileToPreview.url} fileName={fileToPreview.name} fileContentType={fileToPreview.type} onClose={handleCloseFilePreview} />
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SCDashboard;